import axios from 'axios';

import { COREAPI, encodeData, getUserData, ISSESSION } from '../constants/global';

export interface TrackPing {
	latitude: number;
	longitude: number;
	accuracy: number;
	timestamp: string;
	entityCode?: string;
}

/**
 * Trade the refresh token for a new session, as the Redux `apiRequest` does before any call.
 *
 * <p>Tracking runs all day on a timer; without this the first ping after the access token expires
 * (an hour, by default) gets a 401 and every later one does too, so the desk loses sight of the agent
 * while the pill can only say "not sent".</p>
 */
const refreshSession = async (): Promise<boolean> => {
	const { refreshToken } = getUserData();
	if (!refreshToken) return false;
	try {
		const res = await axios.post(`${COREAPI}/api/v1/Auth/extend-session`, null, {
			timeout: 20_000,
			validateStatus: () => true,
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${refreshToken}` },
		});
		const token = res.data?.data?.token;
		if (res.status < 200 || res.status >= 300 || !token) return false;
		encodeData(token, res.data?.data?.refreshToken);
		return true;
	} catch {
		return false;
	}
};

const send = async (ping: TrackPing): Promise<number> => {
	const { token } = getUserData();
	if (!token) return 401;
	try {
		const res = await axios.post(`${COREAPI}/api/v1/track`, ping, {
			timeout: 20_000,
			validateStatus: () => true,
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		});
		return res.status;
	} catch {
		return 0;
	}
};

/**
 * `POST /api/v1/Track` without the global toast.
 *
 * <p>The Redux action shows "Something went wrong" on any failure, which told a field agent nothing
 * about location and nothing they could fix. A ping is retried by the next one anyway, so the tracker
 * reports a failed send in its own pill instead. No PartyId header: api.core reads the party (or its
 * absence, for an independent observer) from the token.</p>
 */
export const postTrackPing = async (ping: TrackPing): Promise<boolean> => {
	if (!ISSESSION()) await refreshSession();
	let status = await send(ping);
	// A token can lapse between the session check and the request: refresh once, send once more.
	if (status === 401 && (await refreshSession())) status = await send(ping);
	return status >= 200 && status < 300;
};
